import { PageTransition } from '@/constant/transition'
import { container } from '@/style/container.css'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { PersonalInfoSchema, type PersonalInfoForm } from './data'
import { fetchLabBaseAuthUserDetails, redirectToLabBaseAuthPage, setJwt } from '@/util/auth'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { cursorPointer } from './style.css'
import * as http from '@/util/http'
import { ProgressFlags } from '@/util/progress'
import { syncProgress } from '../helper'
import { fetchUserData, DEFAULT_PERSONAL_INFO } from './data'
import { queryClient } from '@/util/queryClient'

export default function PersonalInfo() {
  const navigate = useNavigate()
  const { data: userData } = useQuery({
    queryKey: ['current-user'],
    queryFn: fetchUserData,
    staleTime: 1000 * 60 * 60,
  })
  const { data: lbUserDetails } = useQuery({
    queryKey: ['lb-auth-user-details'],
    queryFn: fetchLabBaseAuthUserDetails,
    staleTime: 1000 * 60 * 60,
  })
  const {
    register,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(PersonalInfoSchema),
    values: userData || DEFAULT_PERSONAL_INFO,
    mode: 'onChange',
  })

  const personalInfoMutation = useMutation({
    mutationFn: (payload: PersonalInfoForm) => http.put('/api/v1/user', { payload }),
    onSuccess: () => {
      // setPersonalInfoData(getValues())
      queryClient.invalidateQueries({ queryKey: ['current-user'] })
      syncProgress(ProgressFlags.PROFILE_INIT, false)

      navigate('/onboarding/user-addr', {
        state: { direction: PageTransition.FromRight },
      })
    },
    onError: (err) => console.log(err),
  })

  useEffect(() => {
    setJwt()
    if (lbUserDetails) {
      setValue('email', lbUserDetails.emailAddress)
    }
  }, [setValue, lbUserDetails])

  return (
    <div className={container}>
      <h1>個人情報の入力</h1>
      <Row className="mb-3">
        <Col>
          <FloatingLabel controlId="input-last-name" label="姓" className="mb-3">
            <Form.Control
              {...register('lastName')}
              type="text"
              placeholder="Lab"
              isInvalid={!!errors.lastName}
            />
          </FloatingLabel>
        </Col>
        <Col>
          <FloatingLabel controlId="input-first-name" label="名" className="mb-3">
            <Form.Control
              {...register('firstName')}
              type="text"
              placeholder="任三郎"
              isInvalid={!!errors.firstName}
            />
          </FloatingLabel>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <FloatingLabel
            controlId="input-last-name-furigana"
            label="姓（フリガナ）"
            className="mb-3"
          >
            <Form.Control
              {...register('lastNameFurigana')}
              type="text"
              placeholder="ラブ"
              isInvalid={!!errors.lastNameFurigana}
            />
          </FloatingLabel>
        </Col>
        <Col>
          <FloatingLabel
            controlId="input-first-name-furigana"
            label="名（フリガナ）"
            className="mb-3"
          >
            <Form.Control
              {...register('firstNameFurigana')}
              type="text"
              placeholder="ニンザブロウ"
              isInvalid={!!errors.firstNameFurigana}
            />
          </FloatingLabel>
        </Col>
      </Row>
      <FloatingLabel controlId="input-birthdate" label="生年月日" className="mb-3">
        <Form.Control
          {...register('birthdate')}
          type="date"
          placeholder="2006/4/2"
          isInvalid={!!errors.birthdate}
        />
      </FloatingLabel>
      <FloatingLabel controlId="input-email-addr" label="メール" className="mb-3">
        <Form.Control
          type="tel"
          placeholder="メールアドレス"
          value={lbUserDetails?.emailAddress || ''}
          readOnly
          onClick={() => redirectToLabBaseAuthPage('/onboarding/personal-info', 'update/email')}
          className={cursorPointer}
        />
      </FloatingLabel>
      <FloatingLabel controlId="input-phone-no" label="電話番号（任意）" className="mb-3">
        <Form.Control
          type="tel"
          placeholder="電話番号（任意）"
          value={lbUserDetails?.phoneNumber || ''}
          readOnly
          onClick={() =>
            redirectToLabBaseAuthPage('/onboarding/personal-info', 'phone_number/update')
          }
          className={cursorPointer}
        />
      </FloatingLabel>
      <Stack direction="horizontal">
        <Button
          variant="outline-secondary"
          size="lg"
          onClick={() => {
            redirectToLabBaseAuthPage('/onboarding/personal-info', 'signup/select')
          }}
        >
          ←
        </Button>
        <Button
          variant="outline-primary"
          size="lg"
          className="ms-auto"
          disabled={!!Object.keys(errors).length}
          onClick={handleSubmit(
            (data) => {
              personalInfoMutation.mutate({
                ...data,
                email: lbUserDetails?.emailAddress || '',
              })
            },
            (err) => console.log(err)
          )}
        >
          →
        </Button>
      </Stack>
    </div>
  )
}
